import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PublishedItemsListParams } from '../service/product.service';

/**
 * Single place for the customer “specification + price” filter sidebar.
 *
 * **Panel visibility** (who opens/closes):
 * - Prefer {@link openFilterBar}, {@link closeFilterBar}, {@link toggleFilterBar} for clarity.
 * - Header `app-nav-right` toggles; shell (`admin`) and some pages subscribe to {@link isOpen$} for layout.
 * - The panel closes automatically when the route changes so it does not stay open across pages.
 * - Applied filters are cleared when leaving product-view or a sub-category listing.
 *
 * **Filtration** (how it hits the backend):
 * - Selection is shared in memory: {@link filterSelection$} / {@link getFilterSelectionSnapshot}.
 *   It is cleared when leaving product-view or a sub-category listing (see {@link resetCatalogFilters}).
 * - **Global published catalog** (e.g. CF product list): merge into query params with
 *   {@link applyFiltersToPublishedItemsListParams} before calling `GET .../filtered-publish-list/`.
 * - **Category “All” listing**: `CfCategoryService.getCategoryFilteredPublishList` (same query contract
 *   as global `filtered-publish-list`).
 * - **Category + specification set**: `getProductsByCategoryAndSpecSet` — use
 *   {@link mergeSelectionIntoCategoryProductQueryParams} / {@link ProductSpecificationFilterSidebarService.getCategoryProductFilterQueryParams}.
 */
/** Query fields aligned with `filtered-publish-list` for category product endpoints. */
export interface CategoryProductFilterQuery {
  item_specs?: string;
  price_min?: number;
  price_max?: number;
}

/** Active spec-group chip shown on mobile product list after filters are applied. */
export interface SpecFilterGroupChip {
  groupId: string;
  label: string;
  tokens: string[];
}

export type AppliedFilterChipId = 'search' | 'priceRange' | 'filterBy' | `spec:${string}`;

/** Removable chip shown on the product list and mobile filter sheet. */
export interface AppliedFilterChip {
  id: AppliedFilterChipId;
  label: string;
  groupId?: string;
  tokens?: string[];
}

export interface AppliedFilterChipLabels {
  search: (term: string) => string;
  priceRange: string;
  filterBy: (sort: string) => string;
  sortAsc: string;
  sortDesc: string;
}

export interface AppliedFilterChipBuildInput {
  searchTerm: string;
  filterMode: 'all' | 'order';
  sortOrder: 'asc' | 'desc' | '';
  specGroupChips: SpecFilterGroupChip[];
  labels: AppliedFilterChipLabels;
  priceMin?: number;
  priceMax?: number;
  priceCeiling?: number;
  priceRangeFiltered?: boolean;
}

/** Builds the unified applied-filter chip list (search, sort, price, spec groups). */
export function buildAppliedFilterChips(input: AppliedFilterChipBuildInput): AppliedFilterChip[] {
  const chips: AppliedFilterChip[] = [];
  const term = (input.searchTerm || '').trim();
  if (term) {
    chips.push({ id: 'search', label: input.labels.search(term) });
  }
  const priceFiltered =
    input.priceRangeFiltered ??
    (input.priceMin != null &&
      input.priceMax != null &&
      input.priceCeiling != null &&
      (input.priceMin > 0 || input.priceMax < input.priceCeiling));
  if (priceFiltered) {
    chips.push({ id: 'priceRange', label: input.labels.priceRange });
  }
  if (input.filterMode === 'order' && (input.sortOrder === 'asc' || input.sortOrder === 'desc')) {
    const sortLabel = input.sortOrder === 'asc' ? input.labels.sortAsc : input.labels.sortDesc;
    chips.push({ id: 'filterBy', label: input.labels.filterBy(sortLabel) });
  }
  for (const group of input.specGroupChips) {
    chips.push({
      id: `spec:${group.groupId}`,
      label: group.label,
      groupId: group.groupId,
      tokens: group.tokens,
    });
  }
  return chips;
}

/** Catalog toolbar state shared between product list and the mobile filter sheet. */
export interface CatalogFilterBridge {
  searchTerm: string;
  filterMode: 'all' | 'order';
  sortOrder: 'asc' | 'desc' | '';
  resultCount: number;
}

export interface MobileFiltersApplyPayload {
  searchTerm: string;
  filterMode: 'all' | 'order';
  sortOrder: 'asc' | 'desc' | '';
}

export interface ProductSpecificationFilterSelection {
  /** Inclusive lower bound (currency units, same as quick-select / slider domain). */
  priceMin: number;
  /** Inclusive upper bound. */
  priceMax: number;
  /**
   * Checked specification filters for `item_specs` (comma-separated).
   * Each entry is either a bare template `itemSpec` id (legacy) or
   * `<identifier>|<url-encoded canonical value>` where `<identifier>` is:
   * - template `itemSpec` when the published spec is linked to a definition row, or
   * - `specByItem_pk` for item-only specs (`itemSpec` null), or
   * - `name_en_key` when the API exposes no pk yet.
   * Canonical value prefers English text from `distinct_line_values`.
   */
  selectedItemSpecs: string[];
}

/** Fallback if max-price is not set yet or API fails. */
export const PRODUCT_SPEC_FILTER_PRICE_MAX = 500;

/** Which product listing owns the active filter sidebar (global vs category-scoped API). */
export type FilterListContext =
  | { mode: 'global' }
  | { mode: 'category'; categoryCode: string };

@Injectable({
  providedIn: 'root',
})
export class ProductSpecificationFilterSidebarService {
  private readonly router = inject(Router);

  private readonly open$ = new BehaviorSubject<boolean>(false);

  private readonly defaultSelection: ProductSpecificationFilterSelection = {
    priceMin: 0,
    priceMax: PRODUCT_SPEC_FILTER_PRICE_MAX,
    selectedItemSpecs: [],
  };

  private readonly selection$ = new BehaviorSubject<ProductSpecificationFilterSelection>({
    ...this.defaultSelection,
  });

  /** Emits when the filter sidebar opens or closes. */
  readonly isOpen$: Observable<boolean> = this.open$.asObservable();

  /** Emits when price range or specification checkboxes change. List pages subscribe and reload data. */
  readonly filterSelection$: Observable<ProductSpecificationFilterSelection> =
    this.selection$.asObservable();

  private readonly priceCeiling$ = new BehaviorSubject<number>(PRODUCT_SPEC_FILTER_PRICE_MAX);

  private readonly catalogBridgeSubject$ = new BehaviorSubject<CatalogFilterBridge>({
    searchTerm: '',
    filterMode: 'all',
    sortOrder: '',
    resultCount: 0,
  });

  private readonly activeSpecGroupChipsSubject$ = new BehaviorSubject<SpecFilterGroupChip[]>([]);

  /** Emits when spec-group chips on the product list change. */
  readonly activeSpecGroupChips$ = this.activeSpecGroupChipsSubject$.asObservable();

  /** Maps specification set id → selected `item_specs` tokens (for chip removal). */
  private readonly specGroupTokenIndex$ = new BehaviorSubject<Record<string, string[]>>({});

  private readonly mobileFiltersAppliedSubject$ = new Subject<MobileFiltersApplyPayload>();

  private readonly listContextSubject$ = new BehaviorSubject<FilterListContext>({ mode: 'global' });

  /** Product list → filter sheet (search, sort mode, result count for footer CTA). */
  readonly catalogBridge$: Observable<CatalogFilterBridge> =
    this.catalogBridgeSubject$.asObservable();

  /** Emitted when the mobile sheet footer “Show N results” is pressed. */
  readonly mobileFiltersApplied$: Observable<MobileFiltersApplyPayload> =
    this.mobileFiltersAppliedSubject$.asObservable();

  /** Emits when the owning list switches between global catalog and a category page. */
  readonly listContext$: Observable<FilterListContext> = this.listContextSubject$.asObservable();

  constructor() {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        const fromKey = this.catalogListingKey(this.router.url);
        const toKey = this.catalogListingKey(e.url);
        // Leaving product-view or a sub-category (or switching between them) drops applied filters
        // so a later visit starts from the unfiltered catalog.
        if (fromKey !== null && fromKey !== toKey) {
          this.resetCatalogFilters();
        }
      }
      if (e instanceof NavigationEnd && this.isOpen) {
        this.closeFilterBar();
      }
    });
  }

  /**
   * Stable id for the two customer listings that own this filter.
   * Query params on product-view are ignored so favorite-group links do not wipe filters.
   */
  private catalogListingKey(url: string): string | null {
    const [pathPart, queryPart] = (url || '').split('?');
    const path = (pathPart || '').split('#')[0];
    if (path.includes('/CFProduct/product-view')) {
      return 'product-view';
    }
    const match = path.match(/\/sub-category\/([^/]+)/);
    if (!match) {
      return null;
    }
    const categoryCode = decodeURIComponent(match[1]);
    const subCategory = new URLSearchParams(queryPart || '').get('subCategory') || '';
    return `sub-category:${categoryCode}:${subCategory}`;
  }

  /** Current max for the price range control and for “full range” detection in query merge. */
  getPriceSliderCeiling(): number {
    return this.priceCeiling$.value;
  }

  /** Called from the filter sidebar after `GET .../filtered-publish-list/max-price/` returns. */
  setPriceSliderMax(max: number): void {
    const v =
      Number.isFinite(max) && max > 0 ? Math.max(1, Math.ceil(max)) : PRODUCT_SPEC_FILTER_PRICE_MAX;
    this.priceCeiling$.next(v);
  }

  get isOpen(): boolean {
    return this.open$.value;
  }

  getFilterSelectionSnapshot(): ProductSpecificationFilterSelection {
    return { ...this.selection$.value, selectedItemSpecs: [...this.selection$.value.selectedItemSpecs] };
  }

  /** Opens the specification filter panel (viewport-docked or in-page, depending on layout). */
  openFilterBar(): void {
    this.open$.next(true);
  }

  /** Closes the specification filter panel. */
  closeFilterBar(): void {
    this.open$.next(false);
  }

  /** Toggles the specification filter panel open/closed. */
  toggleFilterBar(): void {
    this.open$.next(!this.open$.value);
  }

  /** Same as {@link openFilterBar}; kept for short call sites (e.g. templates). */
  open(): void {
    this.openFilterBar();
  }

  /** Same as {@link closeFilterBar}. */
  close(): void {
    this.closeFilterBar();
  }

  /** Same as {@link toggleFilterBar}. */
  toggle(): void {
    this.toggleFilterBar();
  }

  /** Replace full selection (e.g. reset or hydrate from snapshot). */
  setFilterSelection(next: ProductSpecificationFilterSelection): void {
    this.selection$.next({
      priceMin: next.priceMin,
      priceMax: next.priceMax,
      selectedItemSpecs: [...next.selectedItemSpecs],
    });
  }

  private suppressSelectionReload = false;

  /** Sets selection and skips the next `filterSelection$` reload (mobile apply batch). */
  setFilterSelectionSuppressingReload(next: ProductSpecificationFilterSelection): void {
    this.suppressSelectionReload = true;
    this.setFilterSelection(next);
  }

  consumeSelectionReloadSuppression(): boolean {
    const suppressed = this.suppressSelectionReload;
    this.suppressSelectionReload = false;
    return suppressed;
  }

  resetFilterSelection(): void {
    const cap = this.getPriceSliderCeiling();
    this.selection$.next({
      priceMin: 0,
      priceMax: cap,
      selectedItemSpecs: [],
    });
    this.activeSpecGroupChipsSubject$.next([]);
    this.specGroupTokenIndex$.next({});
  }

  /** Clears spec/price selection plus mobile search/sort so the next listing visit is unfiltered. */
  resetCatalogFilters(): void {
    this.suppressSelectionReload = true;
    this.resetFilterSelection();
    this.catalogBridgeSubject$.next({
      searchTerm: '',
      filterMode: 'all',
      sortOrder: '',
      resultCount: 0,
    });
  }

  /** Keeps mobile filter sheet in sync with the product list toolbar. */
  setCatalogBridge(patch: Partial<CatalogFilterBridge>): void {
    this.catalogBridgeSubject$.next({ ...this.catalogBridgeSubject$.value, ...patch });
  }

  getCatalogBridgeSnapshot(): CatalogFilterBridge {
    return { ...this.catalogBridgeSubject$.value };
  }

  /** Sets whether preview/search uses global or category-scoped product APIs. */
  setListContext(context: FilterListContext): void {
    this.listContextSubject$.next(context);
  }

  getListContext(): FilterListContext {
    return this.listContextSubject$.value;
  }

  clearListContext(): void {
    this.setListContext({ mode: 'global' });
  }

  /** Spec-group chips (e.g. “Brand”) derived from sidebar checkbox state. */
  setActiveSpecGroupChips(chips: SpecFilterGroupChip[]): void {
    this.activeSpecGroupChipsSubject$.next([...chips]);
  }

  getActiveSpecGroupChips(): SpecFilterGroupChip[] {
    return [...this.activeSpecGroupChipsSubject$.value];
  }

  setSpecGroupTokenIndex(index: Record<string, string[]>): void {
    this.specGroupTokenIndex$.next({ ...index });
  }

  getSpecGroupTokens(groupId: string): string[] {
    return [...(this.specGroupTokenIndex$.value[groupId] ?? [])];
  }

  /** Whether the current price range differs from the full slider span. */
  isPriceRangeFiltered(): boolean {
    const sel = this.getFilterSelectionSnapshot();
    const cap = this.getPriceSliderCeiling();
    return sel.priceMin > 0 || sel.priceMax < cap;
  }

  /** Clears only the price range portion of the selection. */
  clearPriceRangeFilter(): void {
    const cap = this.getPriceSliderCeiling();
    const snap = this.getFilterSelectionSnapshot();
    this.suppressSelectionReload = true;
    this.setFilterSelection({
      ...snap,
      priceMin: 0,
      priceMax: cap,
    });
  }

  /** Removes all selected values belonging to one specification set group. */
  clearSpecGroupFilter(groupId: string, tokensInGroup: string[]): void {
    const snap = this.getFilterSelectionSnapshot();
    const chipEntry = this.activeSpecGroupChipsSubject$.value.find((c) => c.groupId === groupId);
    const resolvedTokens =
      tokensInGroup.length > 0
        ? tokensInGroup
        : chipEntry?.tokens?.length
          ? chipEntry.tokens
          : this.getSpecGroupTokens(groupId);
    const drop = new Set(resolvedTokens.map((t) => String(t).trim()).filter(Boolean));
    const nextSpecs = snap.selectedItemSpecs.filter((t) => !drop.has(t));
    this.suppressSelectionReload = true;
    this.setFilterSelection({
      ...snap,
      selectedItemSpecs: nextSpecs,
    });
    this.activeSpecGroupChipsSubject$.next(
      this.activeSpecGroupChipsSubject$.value.filter((c) => c.groupId !== groupId)
    );
    const nextIndex = { ...this.specGroupTokenIndex$.value };
    delete nextIndex[groupId];
    this.specGroupTokenIndex$.next(nextIndex);
  }

  /** Commits mobile sheet draft (search + sort mode) and closes the panel. */
  applyMobileFilters(
    payload: MobileFiltersApplyPayload,
    selection: ProductSpecificationFilterSelection
  ): void {
    this.setFilterSelectionSuppressingReload(selection);
    this.mobileFiltersAppliedSubject$.next(payload);
    this.closeFilterBar();
  }

  /**
   * Applies the current filter selection to params for `ProductService.getPublishedItemsList`
   * (`/CF/filtered-publish-list/`). Pass a `base` that already includes `page`, `page_size`, sort,
   * and optional `item` search — this method adds `item_specs`, `price_min`, `price_max` when needed.
   */
  applyFiltersToPublishedItemsListParams(base: PublishedItemsListParams): PublishedItemsListParams {
    return mergeSelectionIntoPublishedItemsListParams(
      this.getFilterSelectionSnapshot(),
      base,
      this.getPriceSliderCeiling()
    );
  }

  /** Snapshot for category pages: same item_spec + price rules as the global published list. */
  getCategoryProductFilterQueryParams(): CategoryProductFilterQuery {
    return mergeSelectionIntoCategoryProductQueryParams(
      this.getFilterSelectionSnapshot(),
      this.getPriceSliderCeiling()
    );
  }
}

/**
 * Pure merge: use in tests or when you have a snapshot without reading the service state.
 */
export function mergeSelectionIntoPublishedItemsListParams(
  sel: ProductSpecificationFilterSelection,
  base: PublishedItemsListParams,
  priceCeiling: number = PRODUCT_SPEC_FILTER_PRICE_MAX
): PublishedItemsListParams {
  const out: PublishedItemsListParams = { ...base };
  const specs = sel.selectedItemSpecs.map((t) => String(t).trim()).filter(Boolean);
  if (specs.length) {
    out.item_specs = specs.join(',');
  } else {
    delete out.item_specs;
  }
  const defaultPriceRange = sel.priceMin <= 0 && sel.priceMax >= priceCeiling;
  if (!defaultPriceRange) {
    out.price_min = sel.priceMin;
    out.price_max = sel.priceMax;
  } else {
    delete out.price_min;
    delete out.price_max;
  }
  return out;
}

/** Pure merge for category product APIs (no `PublishedItemsListParams` base). */
export function mergeSelectionIntoCategoryProductQueryParams(
  sel: ProductSpecificationFilterSelection,
  priceCeiling: number = PRODUCT_SPEC_FILTER_PRICE_MAX
): CategoryProductFilterQuery {
  return mergeSelectionIntoPublishedItemsListParams(sel, {}, priceCeiling) as CategoryProductFilterQuery;
}
